import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { MessageMedium } from 'constants/messageMediums';
import { SAVED_ROW_LIMIT, SAVED_ROW_LIMIT_ERROR } from 'modules/dragAndDropEditor/constants';
import { useDragAndDropEditorContext } from 'modules/dragAndDropEditor/context';
import { SavedRow } from 'modules/dragAndDropEditor/types';
import { GhostButton, PrimaryButton } from 'ui/button';
import { WarningFill16 } from 'ui/icons';
import { caption, colors, spacing } from 'ui/theme';
import { TipPlacement, Tooltip } from 'ui/tooltip';
import noop from 'utils/noop';
import { CreateOrEditSavedRowFormValues } from './CreateOrEditSavedRowFormProvider';
import {
  generateCreateSavedRowPayload,
  generateUpdateSavedRowPayload,
} from './CreateOrEditSavedRowSlideOver.utils';

const FooterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const UnsavedChanges = styled.div`
  ${caption};
  color: ${colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
`;

const Actions = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

export const SAVE_ROW_BUTTON_ID = 'save-row-button';
export const CANCEL_BUTTON_ID = 'cancel-save-row-button';
export const UNSAVED_CHANGES_ID = 'unsaved-changes';

export type CreateOrEditSavedRowSlideOverFooterProps = {
  savedRow?: SavedRow;
  isEditingRow: boolean;
  /** Callback to run after closing the slideover */
  onCloseCallback?: () => void;
  onRejectEditBeeRow: () => void;
  onResolveEditBeeRow: (args: any) => void;
  onCloseConfirmation?: () => void;
};

export const CreateOrEditSavedRowSlideOverFooter = ({
  savedRow,
  isEditingRow,
  onRejectEditBeeRow,
  onResolveEditBeeRow,
  onCloseConfirmation = noop,
  onCloseCallback = noop,
}: CreateOrEditSavedRowSlideOverFooterProps) => {
  const {
    isCreatingSavedRow,
    isUpdatingSavedRow,
    updateSavedRow,
    createSavedRow,
    messageMedium,
    parseSavedRowProcess,
    savedRowCount,
  } = useDragAndDropEditorContext();

  const {
    handleSubmit,
    formState: { isDirty },
  } = useFormContext<CreateOrEditSavedRowFormValues>();

  const onCreate = (values: CreateOrEditSavedRowFormValues) => {
    const additionalData = {
      messageMedium: messageMedium as unknown as MessageMedium,
      html: parseSavedRowProcess?.data?.html as string,
      beeMetadata: parseSavedRowProcess?.data?.beeBuilderMetadata as string,
    };

    createSavedRow({
      variables: generateCreateSavedRowPayload(values, additionalData),
      onCompleted: () => {
        onCloseCallback();
      },
    });
  };

  const onEdit = (values: CreateOrEditSavedRowFormValues) => {
    updateSavedRow({
      variables: generateUpdateSavedRowPayload(values, savedRow as SavedRow),
      onError: () => onRejectEditBeeRow(),
      onCompleted: () => onResolveEditBeeRow(true),
    });
  };

  const onSave = isEditingRow ? onEdit : onCreate;
  const rowCountReached = savedRowCount ? savedRowCount >= SAVED_ROW_LIMIT && !isEditingRow : false;

  return (
    <FooterContainer>
      <UnsavedChanges data-test={UNSAVED_CHANGES_ID}>
        {isDirty && (
          <>
            <WarningFill16 color={colors.warning100} />
            There are unsaved changes
          </>
        )}
      </UnsavedChanges>
      <Actions>
        <GhostButton data-test={CANCEL_BUTTON_ID} onClick={onCloseConfirmation}>
          Cancel
        </GhostButton>
        <Tooltip
          isEnabled={rowCountReached}
          placement={TipPlacement.Left}
          tooltipContent={SAVED_ROW_LIMIT_ERROR}
        >
          <PrimaryButton
            data-test={SAVE_ROW_BUTTON_ID}
            isDisabled={rowCountReached}
            isLoading={isCreatingSavedRow || isUpdatingSavedRow}
            onClick={handleSubmit(onSave)}
          >
            Save
          </PrimaryButton>
        </Tooltip>
      </Actions>
    </FooterContainer>
  );
};
